import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { connect } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import {
  CompositeDecorator,
  ContentState,
  convertFromRaw,
  EditorState,
} from "draft-js";

import {
  axiosInstance,
  createPost,
  getAccount,
  getTooltip,
  trackEvents,
  updateTooltip,
} from "api";
import useTimezone from "hooks/useTimezone";
import {
  getAspectRatio,
  getFileSize,
  getImageDimensions,
  getVideoDimensions,
  mediaIsImage,
  showAlert,
} from "utils/utils";
import { parseMediaType } from "utils/postContent";
import { fileLimits } from "constants/post";
// Import validation functions
import {
  validateTwitter,
  validateFacebook,
  validateInstagram,
  validateLinkedin,
  validateYoutube,
  validateTiktok,
  validatePinterest,
  validateMetaThreads,
} from "utils/validation";

export const PostContext = createContext(null);

const platformMediaInitialState = {
  default: [],
  facebook: [],
  linkedin: [],
  instagram: [],
  twitter: [[]],
  youtube: [],
  tiktok: [],
  pinterest: [],
  meta_threads: [[]],
};

const defaultContent = {
  caption: "",
  postType: "post",
};

const contentByPlatformInitialState = {
  default: defaultContent,
  facebook: defaultContent,
  linkedin: defaultContent,
  instagram: defaultContent,
  twitter: { ...defaultContent, thread: [{ content: "", postType: "thread" }] },
  youtube: {
    ...defaultContent,
    title: "",
    visibility: "public",
    made_for_kids: undefined,
  },
  tiktok: defaultContent,
  pinterest: {
    ...defaultContent,
    title: "",
  },
  meta_threads: {
    ...defaultContent,
    thread: [{ content: "", postType: "thread" }],
  },
};

const contentByPlatformReducer = (state, action) => {
  if (action.type === "RESET") return contentByPlatformInitialState;
  if (!action.platform) return state;

  return {
    ...state,
    [action.platform]: action.payload,
  };
};

const findMentionEntities = (contentBlock, callback, contentState) => {
  const text = contentBlock.getText();
  const MENTION_REGEX = /(@[\w]+)/g;
  let matchArr, start;
  while ((matchArr = MENTION_REGEX.exec(text)) !== null) {
    start = matchArr.index;
    callback(start, start + matchArr[0].length);
  }
};

const findHashtagEntities = (contentBlock, callback, contentState) => {
  const text = contentBlock.getText();
  const HASHTAG_REGEX = /(#[\w\u0590-\u05ff]+)/g;
  let matchArr, start;
  while ((matchArr = HASHTAG_REGEX.exec(text)) !== null) {
    start = matchArr.index;
    callback(start, start + matchArr[0].length);
  }
};

const findLinks = (contentBlock, callback, contentState) => {
  const text = contentBlock.getText();
  const regex = /(https?:\/\/\S+)/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    callback(match.index, match.index + match[0].length);
  }
};

const LinkSpan = (props) => (
  <a href={props.children[0].props.text} style={{ color: "#1DA1F2" }}>
    {props.children}
  </a>
);

const Mention = (props) => {
  return (
    <span style={{ color: "#4a9eff" }} data-offset-key={props.offsetKey}>
      {props.children}
    </span>
  );
};

const Hashtag = (props) => {
  return (
    <span style={{ color: "#1da1f2" }} data-offset-key={props.offsetKey}>
      {props.children}
    </span>
  );
};

export const defaultDecorators = [
  {
    strategy: findMentionEntities,
    component: Mention,
  },
  {
    strategy: findHashtagEntities,
    component: Hashtag,
  },
  {
    strategy: findLinks,
    component: LinkSpan,
  },
];

const decorator = new CompositeDecorator(defaultDecorators);

const PostContextProvider = ({
  children,
  brand,
  active_account,
  user,
  onboarding_state,
}) => {
  const allChannels = brand?.socials?.socials || [];
  const { timezones } = useTimezone();

  const router = useHistory();
  const { search } = useLocation();
  const calendarScheduleDate = new URLSearchParams(search).get("schedule_date");
  const redirectPath = new URLSearchParams(search).get("return_to");
  const postId = new URLSearchParams(search).get("postId");
  const { getSelectedTimezone, getTimezoneGMT } = useTimezone();
  const [postDate, setPostDate] = useState(new Date());
  const [timezone, setTimezone] = useState(getTimezoneGMT(brand?.timezone));
  const [postDetails, setPostDetails] = useState({});
  const [posting, setPosting] = useState(false);
  const [buttonInfo, setButtonInfo] = useState("");
  const [bestPostTime, setBestPostTime] = useState({});
  const [editImageDetail, setEditImageDetail] = useState(null);
  const [openKey, setOpenKey] = useState(0);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [selectedBrandChannels, setSelectedBrandChannels] = useState();
  const [contentByPlatform, contentByPlatformDispatch] = useReducer(
    contentByPlatformReducer,
    contentByPlatformInitialState
  );
  const [platformMedia, setPlatformMedia] = useState(platformMediaInitialState);
  const [imageCaption, setImageCaption] = useState("");
  const [activePlatform, setActivePlatform] = useState("default");
  const [openMediaLibrary, setOpenMediaLibrary] = useState(false);
  const [editorState, setEditorState] = useState(
    EditorState.createEmpty(decorator)
  );
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);

  const parseEditorContent = useCallback(
    (content, customDecorator = decorator) => {
      if (content && content._map) {
        return EditorState.createWithContent(content, customDecorator);
      }

      if (typeof content === "string") {
        try {
          const contentState = convertFromRaw(JSON.parse(content));
          return EditorState.createWithContent(contentState, customDecorator);
        } catch {
          return EditorState.createWithContent(
            ContentState.createFromText(content || ""),
            customDecorator
          );
        }
      }

      return EditorState.createEmpty(customDecorator);
    },
    []
  );

  const validateFileSize = useCallback(
    (file) => {
      const sizeInMb = file.size / 1000 / 1000;
      const mediaType = parseMediaType(file.type);
      const fileLimit = fileLimits[activePlatform][mediaType];
      const fileLimitReel = fileLimits[activePlatform]["reel"];
      const isReel = contentByPlatform[activePlatform].postType === "reel";

      if (isReel && sizeInMb > fileLimitReel) {
        throw new Error(
          `File limit for ${activePlatform} reel is ${getFileSize(
            fileLimitReel * 1000 * 1000
          )}`
        );
      }

      if (sizeInMb > fileLimit) {
        throw new Error(
          `File limit for ${activePlatform} ${mediaType} is ${getFileSize(
            fileLimit * 1000 * 1000
          )}`
        );
      }
    },
    [activePlatform, contentByPlatform]
  );

  const getFileProps = async (media) => {
    if (media.type.includes("image")) {
      const imgDimensions = await getImageDimensions(media.local_path);
      const aspectRatio = getAspectRatio(
        imgDimensions.width,
        imgDimensions.height
      );
      const media_properties = {
        width: imgDimensions.width,
        height: imgDimensions.height,
        aspect_ratio: {
          width: aspectRatio.aspectRatioWidth,
          height: aspectRatio.aspectRatioHeight,
          value: aspectRatio.aspectRatioValue,
        },
      };
      return media_properties;
    } else if (media.type.includes("video")) {
      const vidDimensions = await getVideoDimensions(media.local_path);
      const aspectRatio = getAspectRatio(
        vidDimensions.width,
        vidDimensions.height
      );
      const media_properties = {
        width: vidDimensions.width,
        height: vidDimensions.height,
        length: vidDimensions.length,
        aspect_ratio: {
          width: aspectRatio.aspectRatioWidth,
          height: aspectRatio.aspectRatioHeight,
          value: aspectRatio.aspectRatioValue,
        },
      };
      return media_properties;
    }
  };

  const handleUpdateContent = useCallback(
    (payloadKey, payload) => {
      contentByPlatformDispatch({
        platform: activePlatform,
        payload: {
          ...contentByPlatform[activePlatform],
          [payloadKey]: payload,
        },
      });
    },
    [contentByPlatform, contentByPlatformDispatch, activePlatform]
  );

  const handleUpdateMedia = useCallback((platform, payload) => {
    setPlatformMedia((prev) => ({
      ...prev,
      [platform]: payload,
    }));
  }, []);

  const clearAllMedia = () => {
    setPlatformMedia(platformMediaInitialState);
  };

  const fieldArray = useMemo(() => {
    return (
      contentByPlatform[activePlatform]?.thread ||
      contentByPlatformInitialState.twitter.thread
    );
  }, [contentByPlatform, activePlatform]);

  const setFieldArray = useCallback(
    (fieldItems) => {
      contentByPlatformDispatch({
        platform: "twitter",
        payload: {
          ...contentByPlatform.twitter,
          thread: fieldItems,
        },
      });
    },
    [contentByPlatform, contentByPlatformDispatch]
  );

  const setMetaThreadFieldArray = useCallback(
    (fieldItems) => {
      contentByPlatformDispatch({
        platform: "meta_threads",
        payload: {
          ...contentByPlatform.meta_threads,
          thread: fieldItems,
        },
      });
    },
    [contentByPlatform, contentByPlatformDispatch]
  );

  const setThreadFieldValue = useCallback(
    (index, fieldName, fieldValue, platform = activePlatform) => {
      const fieldItems = structuredClone(fieldArray);
      fieldItems[index][fieldName] = fieldValue;

      if (platform === "twitter") {
        setFieldArray(fieldItems);
      } else if (platform === "meta_threads") {
        setMetaThreadFieldArray(fieldItems);
      }
    },
    [fieldArray, setFieldArray, setMetaThreadFieldArray, activePlatform]
  );

  const addThreadMediaRow = () => {
    if (activePlatform === "twitter") {
      const newArray = [...platformMedia.twitter];
      newArray.push([]);
      setPlatformMedia({ ...platformMedia, twitter: newArray });
    } else if (activePlatform === "meta_threads") {
      const newArray = [...platformMedia.meta_threads];
      newArray.push([]);
      setPlatformMedia({ ...platformMedia, meta_threads: newArray });
    }
  };

  const removeThreadMediaRow = (index) => {
    if (
      activePlatform === "twitter" &&
      platformMedia.twitter[index] !== undefined &&
      platformMedia.twitter[index] !== null
    ) {
      let fieldItems = [...platformMedia.twitter];
      fieldItems.splice(index, 1);
      setPlatformMedia({ ...platformMedia, twitter: fieldItems });
    } else if (
      activePlatform === "meta_threads" &&
      platformMedia.meta_threads[index] !== undefined &&
      platformMedia.meta_threads[index] !== null
    ) {
      let fieldItems = [...platformMedia.meta_threads];
      fieldItems.splice(index, 1);
      setPlatformMedia({ ...platformMedia, meta_threads: fieldItems });
    }
  };

  const platformList = useMemo(() => {
    const platforms = selectedChannels.map((channel) => channel.platform);

    return platforms;
  }, [selectedChannels]);

  // posting privilege
  const postsPerChannel = active_account?.privilege.monthly_post_per_channel;
  const otherAccountMonthlyPosts =
    active_account?.privilege.total_monthly_posts;

  const handleOnSelectChannel = useCallback(
    (platform) => {
      const defaultContent = contentByPlatform.default;
      const platformContent = contentByPlatform[platform];
      const platformMediaList = platformMedia[platform];

      if (
        ["twitter", "meta_threads"].includes(platform) &&
        !platformContent.thread?.[0]?.content &&
        !platformMediaList?.[0]?.length
      ) {
        defaultContent.caption &&
          setThreadFieldValue?.(0, "content", defaultContent.caption, platform);
        platformMedia.default?.length &&
          handleUpdateMedia(platform, [platformMedia["default"]]);
      } else if (!platformContent.caption && !platformMediaList?.length) {
        if (defaultContent.caption?.length > 0) {
          contentByPlatformDispatch({
            platform,
            payload: {
              ...contentByPlatform[platform],
              caption: defaultContent.caption,
            },
          });
          setEditorState(parseEditorContent(defaultContent.caption));
        }

        platformMedia.default?.length &&
          handleUpdateMedia(platform, platformMedia.default);
      }
    },
    [
      contentByPlatform,
      platformMedia,
      setThreadFieldValue,
      handleUpdateMedia,
      contentByPlatformDispatch,
      setEditorState,
      parseEditorContent,
    ]
  );

  // disable submit post
  const disableActions = useMemo(() => {
    const statusList = [];
    const platformsWithErrors = [];
    const mediaLoadingList = [];

    for (const platform of platformList) {
      const {
        title,
        caption,
        privacy_level,
        thread,
        postType,
        made_for_kids,
        tag_users,
        tag_collaborators,
        board_id,
        link,
      } = contentByPlatform[platform];

      const hashtagRegex = /#[\w]+/g;
      const hashtags = caption?.match(hashtagRegex);

      const { twitter, meta_threads, ...filteredMediaData } = platformMedia;
      const mediaList = filteredMediaData[platform];

      const monthlyPosts = active_account.privilege.posting_is_per_channel
        ? postsPerChannel[platform]
        : otherAccountMonthlyPosts;

      mediaList?.forEach((item) => mediaLoadingList?.push(!!item?.path));

      if (platform === "youtube") {
        const validationResult = validateYoutube({
          title,
          caption,
          mediaList,
          postType,
          made_for_kids,
          monthlyPosts,
        });

        statusList.push(validationResult);
        validationResult && platformsWithErrors.push("youtube");
      } else if (platform === "tiktok") {
        const validationResult = validateTiktok({
          caption,
          privacy_level,
          mediaList,
          monthlyPosts,
          fileLimits: fileLimits[platform],
        });

        statusList.push(validationResult);
        validationResult && platformsWithErrors.push("tiktok");
      } else if (platform === "twitter" && !!thread?.length) {
        const { hasError, mediaLoadingList: newMediaLoadingList } =
          validateTwitter({
            thread,
            platformMedia,
            monthlyPosts,
          });

        statusList.push(hasError);
        hasError && platformsWithErrors.push("twitter");
        mediaLoadingList.push(...newMediaLoadingList);
      } else if (platform === "meta_threads" && !!thread?.length) {
        const { hasError, mediaLoadingList: newMediaLoadingList } =
          validateMetaThreads({
            thread,
            platformMedia,
            monthlyPosts,
          });

        statusList.push(hasError);
        hasError && platformsWithErrors.push("meta_threads");
        mediaLoadingList.push(...newMediaLoadingList);
      } else if (platform === "facebook") {
        const validationResult = validateFacebook({
          caption,
          postType,
          mediaList,
          monthlyPosts,
        });

        statusList.push(validationResult);
        validationResult && platformsWithErrors.push("facebook");
      } else if (platform === "linkedin") {
        const validationResult = validateLinkedin({
          caption,
          mediaList,
          monthlyPosts,
        });

        statusList.push(validationResult);
        validationResult && platformsWithErrors.push("linkedin");
      } else if (platform === "instagram") {
        const validationResult = validateInstagram({
          caption,
          postType,
          mediaList,
          hashtags,
          tag_users,
          tag_collaborators,
          monthlyPosts,
        });

        statusList.push(validationResult);
        validationResult && platformsWithErrors.push("instagram");
      } else if (platform === "pinterest") {
        const validationResult = validatePinterest({
          title,
          caption,
          link,
          mediaList,
          board_id,
          monthlyPosts,
        });

        statusList.push(validationResult);
        validationResult && platformsWithErrors.push("pinterest");
      }
    }

    return {
      status:
        posting ||
        !statusList.length ||
        statusList.includes(true) ||
        mediaLoadingList?.includes(false),
      platformsWithErrors,
      isMediaUploading: mediaLoadingList?.includes(false),
    };
  }, [
    posting,
    platformList,
    contentByPlatform,
    active_account.privilege.posting_is_per_channel,
    postsPerChannel,
    otherAccountMonthlyPosts,
    platformMedia,
  ]);

  const disableDraftSubmit = useMemo(() => {
    let statusList = [];
    for (const platform of platformList) {
      const { caption, thread } = contentByPlatform[platform];

      const { twitter, meta_threads, ...filteredMediaData } = platformMedia;
      const mediaList = filteredMediaData[platform];

      if (
        !caption &&
        !mediaList?.length &&
        platform !== "twitter" &&
        platform !== "meta_threads"
      ) {
        statusList.push(true);
      }

      if (platform === "twitter") {
        (thread ?? []).forEach((item, idx) => {
          const tweet = item?.content;
          const twitterMedia = twitter[idx];

          if (!tweet && !twitterMedia?.length) {
            statusList.push(true);
          }
        });
      }

      if (platform === "meta_threads") {
        (thread ?? []).forEach((item, idx) => {
          const post = item?.content;
          const threadsMedia = meta_threads[idx];

          if (!post && !threadsMedia?.length) {
            statusList.push(true);
          }
        });
      }
    }

    return statusList.includes(true) || !platformList.length;
  }, [contentByPlatform, platformList, platformMedia]);

  // trigger prompt when leaving the post page
  const triggerPageLeavePrompt = useMemo(() => {
    const defaultContent = contentByPlatform["default"];
    let trigger;
    for (const platform of platformList) {
      const { title, caption, thread } = contentByPlatform[platform];

      const { twitter, meta_threads, ...filteredMediaData } = platformMedia;
      const mediaList = filteredMediaData[platform];

      const twitterMediaList = thread?.map((item, idx) => {
        const mediaList = platformMedia.twitter[idx];
        return mediaList;
      });

      const metaThreadsMediaList = thread?.map((item, idx) => {
        const mediaList = platformMedia.meta_threads[idx];
        return mediaList;
      });

      if (
        caption ||
        title ||
        !!mediaList?.length ||
        (thread && thread[0]?.content) ||
        (thread && !!twitterMediaList?.length) ||
        (thread && !!metaThreadsMediaList?.length)
      ) {
        trigger = true;
      }
    }

    if (defaultContent.caption || platformMedia["default"]?.length) {
      trigger = true;
    }

    return trigger;
  }, [contentByPlatform, platformList, platformMedia]);

  const getPostDetails = (action) => {
    // const timezoneName = timezone.replace(/^\([^)]+\)\s*/, "");
    const data = {};
    data.post = [];
    data.selectedChannels = selectedChannels;
    data.accountId = active_account?.id;
    data.gmt_offset = -new Date().getTimezoneOffset() / 60;
    data.brandId = brand?.id;
    data.tz = timezone;
    data.timezone = getSelectedTimezone(timezone)?.timezone;
    data.type = 1; // 1 = IMMEDIATE, 2 = SCHEDULED, 3 = QUEUED

    if (action === "schedule") {
      data.schedule_date = Date.parse(postDate);
      data.type = 2;
    }

    if (action === "queue") {
      data.type = 3;
    }

    for (const channel of selectedChannels) {
      const { platform } = channel;
      const {
        title,
        caption,
        postType,
        duet_disabled,
        stitch_disabled,
        comment_disabled,
        privacy_level,
        thread,
        first_comment,
        tag_location,
        share_to_feed,
        visibility,
        made_for_kids,
        tag_users,
        tag_collaborators,
        tag_info,
        board_id,
        link,
        shareable,
      } = contentByPlatform[platform];

      const { twitter, meta_threads, ...filteredMediaData } = platformMedia;
      const mediaList = filteredMediaData[platform];

      const postData = {
        platform,
        platform_post_type: postType,
        media: mediaList?.map((item) => item.path),
        timezone: data.timezone,
        content: caption,
        type: data.type,
        shareable: shareable ?? true,
      };

      //this is for when user decided to use suggested post on user homepage
      if (postId) {
        postData.ai_post_idea = postId;
      }

      const threadData = thread?.map((item, idx) => {
        return {
          platform: "twitter",
          platform_post_type: thread?.length > 1 ? "thread" : "post",
          timezone: data.timezone,
          type: data.type,
          media: platformMedia.twitter[idx]?.map((d) => d.path),
          content: item.content,
        };
      });

      const metaThreadData = thread?.map((item, idx) => {
        return {
          platform: "meta_threads",
          platform_post_type: thread?.length > 1 ? "thread" : "post",
          timezone: data.timezone,
          type: data.type,
          media: platformMedia.meta_threads[idx]?.map((d) => d.path),
          content: item.content,
        };
      });

      if (action === "schedule") {
        let scheduleDate;
        if (platform === "facebook") {
          scheduleDate = bestPostTime?.["facebook"] ?? postDate;
          postData.schedule_date = Date.parse(scheduleDate) / 1000;
        } else if (platform === "instagram") {
          scheduleDate = bestPostTime?.["instagram"] ?? postDate;
          postData.schedule_date = Date.parse(scheduleDate) / 1000;
        } else if (platform === "tiktok") {
          scheduleDate = bestPostTime?.["tiktok"] ?? postDate;
          postData.schedule_date = Date.parse(scheduleDate) / 1000;
        } else if (platform === "linkedin") {
          scheduleDate = bestPostTime?.["linkedin"] ?? postDate;
          postData.schedule_date = Date.parse(scheduleDate) / 1000;
        } else if (platform === "youtube") {
          scheduleDate = bestPostTime?.["youtube"] ?? postDate;
          postData.schedule_date = Date.parse(scheduleDate) / 1000;
        }
      }

      if (platform === "youtube") {
        postData.title = title;
        postData.meta = {
          visibility: visibility ?? "public",
          made_for_kids: made_for_kids,
        };
      }

      if (platform === "tiktok") {
        postData.post_config = {
          privacy_level: privacy_level,
          duet_disabled: duet_disabled ?? false,
          comment_disabled: comment_disabled ?? false,
          stitch_disabled: stitch_disabled ?? false,
        };
      }

      if (platform === "twitter") {
        threadData.forEach((item) => {
          if (action === "schedule") {
            let scheduleDate = bestPostTime?.[platform] ?? postDate;
            item.schedule_date = Date.parse(scheduleDate) / 1000;
          }
          return data.post.push(item);
        });
      } else if (platform === "meta_threads") {
        metaThreadData.forEach((item) => {
          if (action === "schedule") {
            let scheduleDate = bestPostTime?.[platform] ?? postDate;
            item.schedule_date = Date.parse(scheduleDate) / 1000;
          }
          return data.post.push(item);
        });
      } else {
        data.post.push(postData);
      }

      if (platform === "instagram") {
        postData.meta = {
          share_to_feed: share_to_feed ?? false,
        };

        if (first_comment) {
          postData.meta.first_comment = first_comment;
        }
        if (tag_location) {
          postData.meta.tag_location = tag_location;
        }
        if (tag_users) {
          postData.meta.tag_users = tag_users;
        }
        if (tag_collaborators) {
          postData.meta.tag_collaborators = tag_collaborators;
        }
      }

      if (platform === "facebook") {
        postData.meta = postData.meta || {};

        if (tag_info) {
          postData.meta.facebook = postData.meta.facebook || {};
          postData.meta.facebook.tag_info = tag_info || {};

          for (const [key, value] of Object.entries(tag_info)) {
            const regex = new RegExp(`@${value}`, "g");
            postData.content = postData.content.replace(regex, key);
          }
        }
      }

      if (platform === "pinterest") {
        postData.title = title;
        postData.platform_post_type = "pin";
        postData.post_config = {
          board_id: board_id,
          use_post_for_all_media: true,
          link: link,
        };
      }
    }

    setPostDetails(data);

    return data;
  };

  const trackCreatePost = async (postData, eventName = "created_post") => {
    const socials = postData?.post?.map((item) => item?.platform);
    const isEven = user?.id % 2 === 0;

    const data = {
      event_name: eventName,
      event_properties: {
        date: new Date(),
        signup_date: active_account?.owner?.joined_at,
        social_media: socials,
        post_type:
          postData?.type === 1
            ? "Post now"
            : postData?.type === 2
            ? "Scheduled post"
            : "Added to queue",
        account_type: active_account?.current_subscription?.plan_name,
        user_type: active_account?.industry,
        post_data: postData,
        onboarding_type: isEven ? "Create post" : "AI tool",
      },
    };

    try {
      await trackEvents(data);
    } catch (error) {
      console.log(error);
    }
  };

  const __createPost = async (action) => {
    let data = action ? getPostDetails(action) : getPostDetails();
    setPosting(true);

    try {
      let res = await createPost(data);

      if (res.data?.status === true) {
        trackCreatePost(data);
        // update setup guide
        if (!onboarding_state.post_to_above_two_channels) {
          const isPostedToMoreThanOneChannel = data.selectedChannels.length > 1;
          const resUpdateTooltip = await updateTooltip({
            ...onboarding_state,
            post_to_above_two_channels: isPostedToMoreThanOneChannel,
          });
          if (resUpdateTooltip.data?.status === true) {
            getTooltip();
          }
        }
        showAlert({
          type: "success",
          msg: `${
            action === `schedule`
              ? "Post scheduled"
              : action === `queue`
              ? "Post added to queue"
              : "Processing post"
          }`,
        });
        router.push(
          redirectPath
            ? decodeURIComponent(redirectPath)
            : "/dashboard/posts/calendar-view"
        );
        getAccount();
      } else if (res?.data?.status === false) {
        showAlert({
          type: "danger",
          msg: res.data.message,
        });
        // track failed post
        await trackCreatePost(data, "failed_post");
      }
      setPosting(false);
    } catch (e) {
      showAlert({
        type: "danger",
        msg:
          e.response?.data?.message ||
          "An error occurred. Could not create post",
      });
      setPosting(false);
      // track failed post
      await trackCreatePost(data, "failed_post");
    }
  };

  const saveDraft = async () => {
    const draftDetails = getPostDetails();
    const postData = {
      draft: draftDetails.post,
    };

    if (selectedWorkflow.id) {
      postData.post[0].approval_workflow_id = selectedWorkflow?.id;
    }

    setPosting(true);
    try {
      const res = await axiosInstance.post(
        `brand/${brand?.id}/drafts`,
        postData
      );
      if (res.data.status === true) {
        // update setup guide
        if (!onboarding_state.create_draft) {
          const resUpdateTooltip = await updateTooltip({
            ...onboarding_state,
            create_draft: true,
          });
          if (resUpdateTooltip.data?.status === true) {
            await getTooltip();
          }
        }
        showAlert({
          type: "success",
          msg: res.data.message,
        });
        router.push("/dashboard/posts/list-view?tab=draft");
      } else {
        showAlert({
          type: "danger",
          msg: res.data.message,
        });
      }
      setPosting(false);
      return res;
    } catch (error) {
      setPosting(false);
      showAlert({
        type: "danger",
        msg: error.response ? error.response.data.message : error.message,
      });
    }
  };

  const duplicatePost = (data, callback) => {
    if (!data) return;

    const { content, media, schedule_date } = data;

    const postData = {
      caption: content,
      postType: "post",
    };

    const mediaList = media?.map((path) => ({
      path: path,
      id: uuidv4(),
      type: mediaIsImage(path) ? "image" : "video",
      errors: [],
    }));

    setEditorState(parseEditorContent(postData.caption));
    contentByPlatformDispatch({ platform: "default", payload: postData });
    handleUpdateMedia("default", mediaList);

    setTimezone((prev) => {
      const timezoneName = timezone;
      const tz = timezones.find((t) => t.timezone === timezoneName)?.name;

      return tz || prev;
    });

    setPostDate(schedule_date ? schedule_date * 1000 : Date.parse(new Date()));

    if (typeof callback === "function") callback();
  };

  const handleRedirect = () => {
    setSelectedChannels([]);
    router.push(
      redirectPath ? decodeURIComponent(redirectPath) : "/dashboard/posts"
    );
    localStorage.removeItem("quick-post");
  };

  const clearPostFields = () => {
    contentByPlatformDispatch({
      type: "RESET",
    });

    clearAllMedia();
    setOpenKey(0);
    setSelectedChannels([]);
    setSelectedBrandChannels(undefined);
    setImageCaption("");
    setActivePlatform("default");
    setPostDate(new Date());
    setEditorState(EditorState.createEmpty(decorator));
  };

  useEffect(() => {
    if (calendarScheduleDate) {
      setPostDate(new Date(parseInt(calendarScheduleDate)));
    }
    // eslint-disable-next-line
  }, []);

  return (
    <PostContext.Provider
      value={{
        platformMedia,
        handleUpdateMedia,
        addThreadMediaRow,
        removeThreadMediaRow,
        postDate,
        setPostDate,
        timezone,
        setTimezone,
        postDetails,
        setPostDetails,
        posting,
        setPosting,
        getPostDetails,
        __createPost,
        saveDraft,
        allChannels,
        selectedChannels,
        setSelectedChannels,
        selectedBrandChannels,
        setSelectedBrandChannels,
        contentByPlatform,
        contentByPlatformDispatch,
        platformList,
        redirectPath,
        bestPostTime,
        setBestPostTime,
        disableActions,
        disableDraftSubmit,
        buttonInfo,
        setButtonInfo,
        triggerPageLeavePrompt,
        clearPostFields,
        editImageDetail,
        setEditImageDetail,
        openKey,
        setOpenKey,
        clearAllMedia,
        imageCaption,
        setImageCaption,
        activePlatform,
        setActivePlatform,
        openMediaLibrary,
        setOpenMediaLibrary,
        defaultDecorators,
        editorState,
        setEditorState,
        parseEditorContent,
        fieldArray,
        setFieldArray,
        setThreadFieldValue,
        validateFileSize,
        getFileProps,
        handleUpdateContent,
        handleOnSelectChannel,
        duplicatePost,
        setMetaThreadFieldArray,
        handleRedirect,
        selectedWorkflow,
        setSelectedWorkflow,
      }}
    >
      {children}
    </PostContext.Provider>
  );
};

const mapStateToProps = (state) => {
  return {
    brand: state.brand,
    active_account: state.active_account,
    user: state.user,
    onboarding_state: state.guided_tours,
  };
};

export default connect(mapStateToProps)(PostContextProvider);
